// src/pages/home/DoctorHome.jsx
import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import AfriPill from "@/sot/primitives/AfriPill.jsx";
import AvatarThumb68 from "@/sot/primitives/AvatarThumb68.jsx";
import { readAvatarSessionCache, writeAvatarSessionCache } from "@/lib/avatarSessionCache.js";
import {
  normalizeProfileSalutation,
  profileText,
  resolveAddressedProfileName,
  resolveProfileFamilyName,
} from "@/pages/shared/profileAddressedName.js";
import supabase from "@/services/supabaseClient";
import { recoverSponsorPayments } from "@/lib/payments/recovery.js";
import { loadDoctorAppointmentsFeed } from "@/pages/doctors/appointments/lib/doctorAppointmentsData.js";
import FolaOnboardingCard from "@/pages/profile-settings/FolaOnboardingCard.jsx";
import useUnreadNotifications from "@/hooks/useUnreadNotifications.js";
import Card from "@/components/ui/Card.jsx";

const DEV_DOCTOR_PHOTO_KEY = "afrimed:dev:doctor_profile_photo";

const ICON_ANALYTICS = "/icons/doctor/home_content_media/analytics.svg";
const ICON_NOTIFICATIONS = "/icons/doctor/home_content_media/notifications.svg";
const ICON_SETTINGS = "/icons/settings.svg";

// Empty-state icon cluster (right side)
const ICON_HOSPITAL = "/icons/doctor/home_content_media/hospital_Fill.svg"; // 32
const ICON_PERSON = "/icons/doctor/home_content_media/personal_injury_Filled.svg"; // 48
const ICON_SCHEDULE = "/icons/doctor/home_content_media/schedule.svg"; // 32
const ICON_SERVICES = "/icons/doctor/home_content_media/medical_services_filled.svg"; // ✅ avoid chop
const ICON_DATE = "/icons/doctor/home_content_media/date_range.svg"; // 32
const ICON_PILL = "/icons/doctor/home_content_media/pill_Fill.svg"; // 48

const ICON_PROFILE_ADD_PHOTO =
  "/icons/doctor/home_content_media/add_photo_alternate.svg"; // 64

const ICON_CONDITIONS =
  "/icons/doctor/home_content_media/conditions_filled.svg"; // 64

// Appointment row icons
const ICON_CHEVRON = "/icons/doctor/home_content_media/navigate_next.svg";

/**
 * Tabs / Status (stable + semantic)
 */
const TAB_TODAY = "today";
const TAB_UPCOMING = "upcoming";
const TAB_PENDING = "pending";

function resolveUiLocale(languageCode) {
  if (languageCode === "sn") return "sn-ZW";
  if (languageCode === "zu") return "zu-ZA";
  return languageCode || undefined;
}

const CUSTOM_LOCALE_LABELS = {
  sn: {
    monthsShort: ["Nd", "Kk", "Km", "Kb", "Cv", "Ck", "Cg", "Ny", "Gu", "Gm", "Mb", "Zv"],
    weekdaysShort: ["Sv", "Mu", "Cp", "Ct", "Cn", "Cs", "Mg"],
  },
  zu: {
    monthsShort: ["Jan", "Feb", "Mas", "Eph", "Mey", "Jun", "Jul", "Aga", "Sep", "Okt", "Nov", "Dis"],
    weekdaysShort: ["Son", "Mso", "Bil", "Tha", "Sin", "Hla", "Mgq"],
  },
};

function formatUiTime(date, languageCode, localeCode) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  if (languageCode === "sn" || languageCode === "zu") {
    return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
  }
  return date.toLocaleTimeString(localeCode, {
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatUiShortDate(date, languageCode, localeCode) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const custom = CUSTOM_LOCALE_LABELS[languageCode];
  if (custom) {
    return `${custom.weekdaysShort[date.getDay()]} ${date.getDate()} ${custom.monthsShort[date.getMonth()]}`;
  }
  return date.toLocaleDateString(localeCode, {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

function resolveDoctorIdCandidates(...values) {
  const out = [];
  const seen = new Set();
  for (const value of values) {
    const id = String(value || "").trim();
    if (
      !id ||
      seen.has(id) ||
      !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(id)
    ) {
      continue;
    }
    seen.add(id);
    out.push(id);
  }
  return out;
}

/**
 * Snooze (Doctor Gallery nudge)
 */
const SNOOZE_DOCTOR_GALLERY_KEY = "afrimed:snooze:doctor_gallery_nudge_until";

function getSnoozeUntil(key) {
  try {
    const raw = localStorage.getItem(key);
    const n = raw ? Number(raw) : 0;
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

function setSnoozeDays(key, days = 14) {
  const until = Date.now() + days * 24 * 60 * 60 * 1000;
  try {
    localStorage.setItem(key, String(until));
  } catch {
    // ignore
  }
  return until;
}

function startOfTodayMs() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

function endOfTodayMs() {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d.getTime();
}

function appointmentMsFromSnapshot(source) {
  const dateISO =
    source?.selectedDate ||
    source?.dateISO ||
    source?.when?.dateISO ||
    source?.payload?.selectedDate ||
    source?.payload?.dateISO ||
    source?.payload?.when?.dateISO ||
    "";
  const timeHHMM =
    source?.selectedTime ||
    source?.start ||
    source?.when?.time ||
    source?.payload?.selectedTime ||
    source?.payload?.start ||
    source?.payload?.when?.time ||
    "00:00";

  if (dateISO) {
    const local = new Date(`${dateISO}T${timeHHMM}:00`);
    const localMs = local.getTime();
    if (Number.isFinite(localMs)) return localMs;
  }

  const fallback = new Date(
    source?.appointment_date || source?.payload?.appointment_date || ""
  );
  const fallbackMs = fallback.getTime();
  return Number.isFinite(fallbackMs) ? fallbackMs : NaN;
}

function resolveDoctorSpecialty(profile) {
  const fromArrays =
    (Array.isArray(profile?.specialization) ? profile.specialization : null) ||
    (Array.isArray(profile?.specializations) ? profile.specializations : null) ||
    (Array.isArray(profile?.specialties) ? profile.specialties : null) ||
    [];

  if (fromArrays.length > 0) {
    return String(fromArrays[0] || "").trim();
  }

  return String(
    profile?.primary_specialty ||
      profile?.specialty ||
      profile?.specialization ||
      ""
  ).trim();
}

function DoctorAppointmentCard({ items = [], onOpen }) {
  const dividerLeft = "ml-16";

  return (
    <Card
      className="
        mt-5
        overflow-hidden
      "
    >
      {items.map((a, idx) => (
        <React.Fragment key={a.id}>
          <button
            type="button"
            onClick={() => onOpen?.(a)}
              className="
              w-full
              px-4 py-3
              flex items-start gap-3
              text-left
            "
          >
            <AvatarThumb68
              src={a.avatar}
              alt={a.name}
              role="patient"
              shape="circle"
              style={{ width: 44, height: 44 }}
            />

            <div className="min-w-0 flex-1">
              <div className="text-body-large text-on-surface truncate">
                {a.name}
              </div>

              <div className="mt-0.5 flex items-start gap-1.5 text-body-medium text-on-surface-var">
                <img
                  src={a.icon}
                  alt=""
                  className="mt-[1px] h-5 w-5 opacity-75 shrink-0"
                  draggable="false"
                />
                <div className="min-w-0">
                  <div className="truncate">{a.subtitle}</div>
                  <div className="mt-0.5">{a.whenLabel || a.time}</div>
                </div>
              </div>
            </div>

            <img
              src={ICON_CHEVRON}
              alt=""
              className="h-6 w-6 opacity-60 shrink-0"
              draggable="false"
            />
          </button>

          {idx < items.length - 1 && (
            <div
              className={`h-px bg-[rgb(var(--clr-outline-variant))] ${dividerLeft}`}
            />
          )}
        </React.Fragment>
      ))}
    </Card>
  );
}

function EmptyAppointmentsCard({ title, body }) {
  return (
    <Card
      className="
        mt-5
        mb-6
        px-6
        py-6
        overflow-hidden
      "
    >
      {/* Keep content vertically centered; keep card not too tall */}
      <div className="flex items-center justify-between gap-4 min-h-[140px]">
        {/* Left text (centered vertically) */}
        <div className="max-w-[240px]">
          <p className="text-body-large text-on-surface-var leading-[22px]">
            {title}
          </p>
          {body ? (
            <p className="mt-2 text-body-medium text-on-surface-var">{body}</p>
          ) : null}
        </div>

        {/* Right icon cluster (slightly more to the right, not chopped) */}
        <div className="shrink-0 pr-1">
          <div className="grid grid-cols-2 gap-x-6 gap-y-5 items-center justify-items-center">
            <img src={ICON_HOSPITAL} alt="" className="h-8 w-8" draggable="false" />
            <img src={ICON_PERSON} alt="" className="h-12 w-12" draggable="false" />

            <img src={ICON_SCHEDULE} alt="" className="h-8 w-8" draggable="false" />
            <img src={ICON_SERVICES} alt="" className="h-12 w-12" draggable="false" />

            <img src={ICON_DATE} alt="" className="h-8 w-8" draggable="false" />
            <img src={ICON_PILL} alt="" className="h-12 w-12" draggable="false" />
          </div>
        </div>
      </div>
    </Card>
  );
}

export default function DoctorHome() {
  const nav = useNavigate();
  const { languageCode, t } = useLanguage();
  const localeCode = React.useMemo(() => resolveUiLocale(languageCode), [languageCode]);
  const auth = (typeof useAuth === "function" ? useAuth() : {}) || {};
  const user = auth.user || {};
  const profile = auth.profile || user || {};

  const [roleProfile, setRoleProfile] = React.useState(null);
  const nameProfile = roleProfile || profile;
  const fullNameRaw =
    profileText(nameProfile.full_name) ||
    [profileText(nameProfile.first_name), profileText(nameProfile.last_name)].filter(Boolean).join(" ") ||
    profileText(nameProfile.display_name) ||
    "";
  const fullNameCandidate = String(fullNameRaw || "").trim();
  const fullNameLooksLikeEmail =
    fullNameCandidate.includes("@") || /^[^@\s]+\.[^@\s]+$/.test(fullNameCandidate);
  const doctorFallback = t("home.doctor.fallbackName", "Doctor");
  const fullName = fullNameCandidate && !fullNameLooksLikeEmail ? fullNameCandidate : doctorFallback;

  const surname =
    resolveProfileFamilyName(nameProfile) ||
    (fullName !== doctorFallback ? String(fullName).trim().split(/\s+/).slice(-1)[0] : "") ||
    doctorFallback;
  const doctorSalutation = normalizeProfileSalutation("doctor", nameProfile?.salutation) || "Dr.";
  const doctorAddressName =
    resolveAddressedProfileName("doctor", nameProfile, "") ||
    (surname && surname !== doctorFallback
      ? `${doctorSalutation} ${surname}`.trim()
      : doctorFallback);
  const specialty = resolveDoctorSpecialty(profile);
  const authProfilePhotoPath =
    profile?.profile_photo_path ||
    profile?.profile_photo_storage_path ||
    profile?.photo_path ||
    profile?.avatar_path ||
    "";
  const authProfileImageUrl =
    profile?.profile_image_url ||
    profile?.profile_photo_url ||
    profile?.photo_url ||
    profile?.avatar_url ||
    profile?.avatar ||
    "";
  const cachedAvatarSrc = React.useMemo(
    () => readAvatarSessionCache({ role: "doctor", userId: user?.id }),
    [user?.id]
  );
  const [rolePhotoPath, setRolePhotoPath] = React.useState("");
  const [rolePhotoUrl, setRolePhotoUrl] = React.useState("");
  const profilePhotoPath = rolePhotoPath || authProfilePhotoPath;
  const profileImageUrl = rolePhotoUrl || authProfileImageUrl;
  const [resolvedAvatarSrc, setResolvedAvatarSrc] = React.useState(
    () => cachedAvatarSrc || profileImageUrl || ""
  );
  const [displayAvatarSrc, setDisplayAvatarSrc] = React.useState(
    () => cachedAvatarSrc || ""
  );
  const [avatarLoading, setAvatarLoading] = React.useState(
    () => Boolean((profilePhotoPath || profileImageUrl) && !cachedAvatarSrc)
  );
  const hasAvatarCandidate = Boolean(cachedAvatarSrc || profilePhotoPath || profileImageUrl);

  const [todayItems, setTodayItems] = React.useState([]);
  const [upcomingItems, setUpcomingItems] = React.useState([]);
  const [pendingItems, setPendingItems] = React.useState([]);
  const [appointmentsLoading, setAppointmentsLoading] = React.useState(true);
  const [appointmentsError, setAppointmentsError] = React.useState("");
  const [doctorGalleryIsEmpty, setDoctorGalleryIsEmpty] = React.useState(true);
  const [regionalFacilityCount, setRegionalFacilityCount] = React.useState(0);
  const { unreadCount: unreadNotifications } = useUnreadNotifications(user?.id);
  const regionalHealthSummary =
    regionalFacilityCount > 0
      ? t(
          "home.doctor.regional.facilitySummary",
          "{count} mapped facilities and trends",
          { count: regionalFacilityCount }
        )
      : t("home.doctor.regional.defaultSummary", "health trends and conditions");

  React.useEffect(() => {
    if (cachedAvatarSrc) {
      setResolvedAvatarSrc(cachedAvatarSrc);
      setDisplayAvatarSrc(cachedAvatarSrc);
      setAvatarLoading(false);
    }
  }, [cachedAvatarSrc]);

  React.useEffect(() => {
    let alive = true;

    async function hydrateDoctorAvatarFields() {
      if (!user?.id) {
        if (alive) {
          setRolePhotoPath("");
          setRolePhotoUrl("");
        }
        return;
      }

      try {
        const { data, error } = await supabase
          .from("doctor_profiles")
          .select("*")
          .eq("user_id", user.id)
          .maybeSingle();

        if (!alive || error || !data) return;
        setRoleProfile(data);
        setRolePhotoPath(
          String(
            data.profile_photo_path ||
              data.profile_photo_storage_path ||
              data.photo_path ||
              data.avatar_path ||
              ""
          ).trim()
        );
        setRolePhotoUrl(
          String(
            data.profile_image_url ||
              data.profile_photo_url ||
              data.photo_url ||
              data.avatar_url ||
              ""
          ).trim()
        );
      } catch {
        // Non-fatal fallback to auth profile fields.
      }
    }

    hydrateDoctorAvatarFields();
    return () => {
      alive = false;
    };
  }, [user?.id]);

  React.useEffect(() => {
    let alive = true;

    async function resolveAvatar() {
      let next = "";

      if (profilePhotoPath) {
        try {
          const storage = supabase.storage.from("doctor-profile-images");
          const { data, error } = await storage.createSignedUrl(profilePhotoPath, 60 * 60 * 24);
          if (!error && data?.signedUrl) {
            next = data.signedUrl;
          } else {
            const { data: publicData } = storage.getPublicUrl(profilePhotoPath);
            next = publicData?.publicUrl || next;
          }
        } catch {
          // ignore and continue fallback chain
        }
      }

      if (!next && profileImageUrl && /^https?:\/\//i.test(profileImageUrl)) {
        next = profileImageUrl;
      }

      try {
        const devPhoto = localStorage.getItem(DEV_DOCTOR_PHOTO_KEY);
        if (devPhoto) next = devPhoto;
      } catch {
        // ignore
      }

      if (!alive) return;
      if (next) {
        setResolvedAvatarSrc(next);
        writeAvatarSessionCache({ role: "doctor", userId: user?.id, src: next });
      } else if (!cachedAvatarSrc) {
        setResolvedAvatarSrc("");
      }
    }

    resolveAvatar();
    return () => {
      alive = false;
    };
  }, [cachedAvatarSrc, profileImageUrl, profilePhotoPath, user?.id]);

  React.useEffect(() => {
    const candidate = resolvedAvatarSrc || cachedAvatarSrc || "";

    if (!candidate) {
      if (hasAvatarCandidate) {
        setAvatarLoading(true);
      } else {
        setAvatarLoading(false);
        setDisplayAvatarSrc("");
      }
      return undefined;
    }

    let alive = true;
    const probe = new window.Image();
    setAvatarLoading(true);
    probe.onload = () => {
      if (!alive) return;
      setDisplayAvatarSrc(candidate);
      setAvatarLoading(false);
    };
    probe.onerror = () => {
      if (!alive) return;
      setAvatarLoading(false);
    };
    probe.src = candidate;

    return () => {
      alive = false;
    };
  }, [cachedAvatarSrc, hasAvatarCandidate, resolvedAvatarSrc]);

  React.useEffect(() => {
    let alive = true;

    async function loadDoctorHome() {
      if (!user?.id) {
        if (!alive) return;
        setTodayItems([]);
        setUpcomingItems([]);
        setPendingItems([]);
        setDoctorGalleryIsEmpty(true);
        setRegionalFacilityCount(0);
        setAppointmentsLoading(false);
        return;
      }

      try {
        setAppointmentsLoading(true);
        setAppointmentsError("");

        const recoveryPromise = recoverSponsorPayments({
          role: "doctor",
          userId: user?.id,
        });

        const candidateDoctorIds = resolveDoctorIdCandidates(
          user?.id,
          auth?.profile?.user_id,
          auth?.profile?.id,
          auth?.profile?.doctor_id,
        );

        if (candidateDoctorIds.length > 0) {
          const { data: ownProfile } = await supabase
            .from("doctor_profiles")
            .select("id,user_id")
            .in("user_id", candidateDoctorIds)
            .limit(1)
            .maybeSingle();
          if (ownProfile?.id) candidateDoctorIds.push(String(ownProfile.id).trim());
          if (ownProfile?.user_id) candidateDoctorIds.push(String(ownProfile.user_id).trim());
        }

        const resolvedDoctorIds = resolveDoctorIdCandidates(...candidateDoctorIds);

        // The appointment state is the critical path for this panel. Gallery,
        // regional and sponsor-recovery queries enrich the screen, but must not
        // delay the empty/scheduled appointment result by several seconds.
        const appointmentsFeedPromise = loadDoctorAppointmentsFeed({
          doctorId: resolvedDoctorIds,
          includeCancelled: false,
          includeMissed: false,
          limit: 300,
        });
        const enrichmentPromise = Promise.allSettled([
          recoveryPromise,
          supabase
            .from("doctor_profiles")
            .select("gallery_photo_paths")
            .eq("user_id", user.id)
            .maybeSingle(),
          supabase
            .from("healthcare_locations")
            .select("id", { count: "exact", head: true })
            .limit(1),
        ]);

        const appointmentsFeed = await appointmentsFeedPromise;

        if (appointmentsFeed.error) throw appointmentsFeed.error;

        const today = [];
        const upcoming = [];
        const pending = [];
        const nowMs = Date.now();
        const todayStart = startOfTodayMs();
        const todayEnd = endOfTodayMs();

        const feedItems = Array.isArray(appointmentsFeed.items) ? appointmentsFeed.items : [];

        feedItems.forEach((item) => {
          const status = String(item?.status || "").toLowerCase();
          if (status === "completed" || status === "cancelled" || status === "missed") return;

          const ms = appointmentMsFromSnapshot(item?.payload ? { ...item, ...item.payload } : item);
          if (!Number.isFinite(ms)) return;
          const date = new Date(ms);

          if ((status === "scheduled" || status === "pending" || status === "confirmed") && ms < nowMs) {
            return;
          }

          const uiItem = {
            id: String(item.id),
            appointmentId: String(item.appointmentId || item.id),
            name: item.patient_name || t("home.doctor.patientFallback", "Patient"),
            subtitle: item.subtitle || t("home.doctor.consultancyFallback", "Consultancy"),
            time: formatUiTime(date, languageCode, localeCode),
            whenLabel:
              ms >= todayStart && ms <= todayEnd
                ? `${t("home.doctor.appointments.when.today", "Today")} · ${formatUiTime(date, languageCode, localeCode)}`
                : `${formatUiShortDate(date, languageCode, localeCode)} · ${formatUiTime(date, languageCode, localeCode)}`,
            icon: item.icon,
            avatar: item.avatar || "/icons/circular-avatar-placeholder.svg",
            payload: item.payload || null,
          };

          if (status === "scheduled" || status === "pending") {
            pending.push(uiItem);
            return;
          }

          if (status === "in_progress") {
            if (ms >= todayStart && ms <= todayEnd) {
              today.push(uiItem);
              return;
            }
            if (ms > nowMs) {
              upcoming.push(uiItem);
            }
            return;
          }

          if (status === "confirmed") {
            if (ms >= todayStart && ms <= todayEnd) {
              today.push(uiItem);
              return;
            }
            if (ms > nowMs) {
              upcoming.push(uiItem);
            }
            return;
          }

          // Home should only show still-actionable same-day appointments.
          // Appointments earlier today belong to past flows, not the Today pill.
          if (ms > nowMs && ms >= todayStart && ms <= todayEnd) {
            today.push(uiItem);
            return;
          }

          if (ms > nowMs) {
            upcoming.push(uiItem);
          }
        });

        if (!alive) return;
        setTodayItems(today);
        setUpcomingItems(upcoming);
        setPendingItems(pending);

        void enrichmentPromise.then(([recoveryRes, profileGalleryRes, regionalRes]) => {
          if (!alive) return;

          if (recoveryRes.status === "fulfilled" && recoveryRes.value?.lastError?.message) {
            console.warn(
              "[DoctorHome] sponsor recovery skipped:",
              recoveryRes.value.lastError.message,
            );
          }

          const galleryArrayCount =
            profileGalleryRes.status === "fulfilled" &&
            Array.isArray(profileGalleryRes.value?.data?.gallery_photo_paths)
              ? profileGalleryRes.value.data.gallery_photo_paths.filter((x) => String(x || "").trim()).length
              : 0;
          setDoctorGalleryIsEmpty(galleryArrayCount === 0);

          if (regionalRes.status === "fulfilled" && !regionalRes.value?.error) {
            const facilityCount = Number(regionalRes.value?.count || 0);
            setRegionalFacilityCount(facilityCount > 0 ? facilityCount : 0);
          }
        });
      } catch (err) {
        if (!alive) return;
        setTodayItems([]);
        setUpcomingItems([]);
        setPendingItems([]);
        setAppointmentsError(
          err?.message || t("home.doctor.appointments.error", "Could not load appointments.")
        );
      } finally {
        if (alive) setAppointmentsLoading(false);
      }
    }

    loadDoctorHome();

    function handleRefresh() {
      void loadDoctorHome();
    }

    function handleVisibilityChange() {
      if (document.visibilityState === "visible") {
        void loadDoctorHome();
      }
    }

    window.addEventListener("focus", handleRefresh);
    window.addEventListener("afrimed:appointments:changed", handleRefresh);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      alive = false;
      window.removeEventListener("focus", handleRefresh);
      window.removeEventListener("afrimed:appointments:changed", handleRefresh);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [localeCode, t, user?.id]);

  const counts = {
    [TAB_TODAY]: todayItems.length,
    [TAB_UPCOMING]: upcomingItems.length,
    [TAB_PENDING]: pendingItems.length,
  };

  /**
   * Pills (labels derived from counts)
   */
  const appointmentTabs = [
    {
      id: TAB_TODAY,
      label:
        counts[TAB_TODAY] > 0
          ? t("home.doctor.appointments.tabs.todayCount", "Today {count}", {
              count: counts[TAB_TODAY],
            })
          : t("home.doctor.appointments.tabs.today", "Today"),
    },
    {
      id: TAB_UPCOMING,
      label:
        counts[TAB_UPCOMING] > 0
          ? t("home.doctor.appointments.tabs.upcomingCount", "Upcoming {count}", {
              count: counts[TAB_UPCOMING],
            })
          : t("home.doctor.appointments.tabs.upcoming", "Upcoming"),
    },
    {
      id: TAB_PENDING,
      label:
        counts[TAB_PENDING] > 0
          ? t(
              "home.doctor.appointments.tabs.pendingCount",
              "Pending for approval {count}",
              { count: counts[TAB_PENDING] }
            )
          : t("home.doctor.appointments.tabs.pending", "Pending for approval"),
    },
  ];

  const [activeTab, setActiveTab] = React.useState(TAB_TODAY);

  /**
   * ✅ activeTab filters visibleAppointments
   */
  const visibleAppointments =
    activeTab === TAB_TODAY
      ? todayItems
      : activeTab === TAB_UPCOMING
      ? upcomingItems
      : pendingItems;

  /**
   * ✅ Snooze state for doctor gallery nudge (lives in DoctorHome, not inside cards)
   */
  const [doctorGallerySnoozeUntil, setDoctorGallerySnoozeUntil] = React.useState(
    () => getSnoozeUntil(SNOOZE_DOCTOR_GALLERY_KEY)
  );

  const doctorGalleryNudgeSnoozed = Date.now() < doctorGallerySnoozeUntil;

  const showDoctorGalleryNudge = doctorGalleryIsEmpty && !doctorGalleryNudgeSnoozed;

  function snoozeDoctorGalleryNudge(days = 14) {
    const until = setSnoozeDays(SNOOZE_DOCTOR_GALLERY_KEY, days);
    setDoctorGallerySnoozeUntil(until);
  }

  return (
  <div className="w-full">
    <main className="afrimed-rail afrimed-rail--comfortable pt-4 pb-24">
      {/* Top header row: avatar + right actions */}
      <div className="flex items-start justify-between gap-3 mb-6">
        <button
          type="button"
          onClick={() => nav("/settings/doctor")}
          className="inline-flex shrink-0"
          aria-label={t("home.doctor.actions.openProfile", "Open profile")}
        >
          <div
            className="
              h-11 w-11
              rounded-full
              p-[1.5px]
              bg-[rgb(var(--clr-surface-cont-highest))]
              ring-1 ring-[rgba(24,50,119,0.12)]
              shadow-[0_6px_12px_-4px_rgba(29,79,215,0.06),0_3px_6px_-2px_rgba(26,27,35,0.06),0_1.5px_0px_-0.5px_rgba(26,27,35,0.06)]
            "
          >
            <div
              className="
                relative
                h-full w-full
                rounded-full
                overflow-hidden
                bg-[rgb(var(--clr-surface-container-low))]
              "
            >
              <img
                src={displayAvatarSrc || (hasAvatarCandidate ? "" : "/icons/circular-avatar-placeholder.svg")}
                alt={fullName}
                className={[
                  "h-full w-full object-cover",
                  displayAvatarSrc ? "opacity-100" : "opacity-0",
                ].join(" ")}
                draggable="false"
              />
              {!displayAvatarSrc && hasAvatarCandidate ? (
                <div
                  className={[
                    "absolute inset-0 rounded-full",
                    "bg-[linear-gradient(135deg,rgba(var(--clr-surface-cont-highest),1)_0%,rgba(var(--clr-surface-container-high),1)_100%)]",
                    avatarLoading ? "animate-pulse" : "",
                  ].join(" ")}
                  aria-hidden="true"
                />
              ) : null}
              {!displayAvatarSrc && !hasAvatarCandidate ? (
                <img
                  src="/icons/circular-avatar-placeholder.svg"
                  alt={fullName}
                  className="absolute inset-0 h-full w-full object-cover"
                  draggable="false"
                />
              ) : null}
            </div>
          </div>
        </button>

        <div className="flex items-center gap-4 pt-1">
          <button
            type="button"
            onClick={() => nav("/doctor/dashboard")}
            className="inline-flex h-10 w-10 items-center justify-center text-on-surface-var transition-colors hover:text-on-surface"
            aria-label={t("home.doctor.actions.dashboard", "Dashboard")}
          >
            <img src={ICON_ANALYTICS} alt="" className="h-6 w-6" draggable="false" />
          </button>
          <button
            type="button"
            onClick={() => nav("/settings/doctor/notifications")}  // ✅ adjust if your route differs
            className="relative inline-flex h-10 w-10 items-center justify-center text-on-surface-var transition-colors hover:text-on-surface md:hidden"
            aria-label={t("home.doctor.actions.notifications", "Notifications")}
          >
            <img src={ICON_NOTIFICATIONS} alt="" className="h-6 w-6" draggable="false" />

            {/* 🔴 badge (show only when unread > 0) */}
            {unreadNotifications > 0 ? (
              <span
                className="
                  absolute top-[6px] right-[6px]
                  h-2 w-2 rounded-full
                  bg-[rgb(var(--afrimed-error,220,38,38))]
                "
              />
            ) : null}
          </button>
          <button
            type="button"
            onClick={() => nav("/settings/doctor")}
            className="relative inline-flex h-10 w-10 items-center justify-center text-on-surface-var transition-colors hover:text-on-surface md:hidden"
            aria-label={t("home.doctor.actions.settings", "Settings")}
          >
            <img src={ICON_SETTINGS} alt="" className="h-6 w-6" draggable="false" />
          </button>
        </div>
      </div>

      {/* Greeting */}
      <div className="mb-5">
        <div className="text-on-surface text-headline-medium tracking-[-0.2px] text-[28px] leading-[34px] md:text-[32px] md:leading-[38px]">
          {t("home.doctor.welcome", "Welcome {doctorName}", { doctorName: doctorAddressName, surname })}
        </div>
        <div className="mt-1 text-body-medium text-on-surface-var">
          {specialty || t("home.doctor.specialtyFallback", "Doctor")}
        </div>
      </div>

      <div className="mb-6">
        <FolaOnboardingCard role="doctor" placement="doctorHome" />
      </div>

      {/* Your Appointments */}
      <section className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-title-medium text-on-surface">
            {t("home.doctor.appointments.title", "Your Appointments")}
          </h2>
          <button
            type="button"
            onClick={() => nav("/doctors/appointments")}
            className="text-body-medium text-primary"
          >
            {t("home.doctor.actions.viewAll", "View all")}
          </button>
        </div>

        {/* Pills */}
        <div className="mt-3 chips-viewport -mx-1">
          <div className="chips-row px-1">
            {appointmentTabs.map((tab) => (
              <AfriPill
                key={tab.id}
                value={tab.id}
                label={tab.label}
                isActive={tab.id === activeTab}
                onChange={setActiveTab}
                size="medium"
                variant="doctor"
              />
            ))}
          </div>
        </div>

        {/* Content */}
        {appointmentsLoading ? (
          <Card className="mt-5 px-4 py-4 text-body-medium text-on-surface-var">
            {t("home.doctor.appointments.loading", "Loading appointments...")}
          </Card>
        ) : appointmentsError ? (
          <Card className="mt-5 px-4 py-4 text-body-medium text-on-surface-var">
            {t(
              "home.doctor.appointments.loadingError",
              "We are loading your appointments. Please refresh shortly."
            )}
          </Card>
        ) : visibleAppointments.length > 0 ? (
          <DoctorAppointmentCard
            items={visibleAppointments}
            onOpen={(a) => {
              const apptId = a.appointmentId || a.id;
              const focusDate =
                a?.payload?.dateISO ||
                a?.payload?.selectedDate ||
                a?.payload?.when?.dateISO ||
                null;
              nav("/doctors/appointments", {
                state: {
                  focusAppointmentId: apptId,
                  focusDate,
                  preferredView: "day",
                  source: "doctor-home",
                },
              });
            }}
          />
        ) : (
          <EmptyAppointmentsCard
            title={t(
              "home.doctor.appointments.emptyTitle",
              "You don't have any scheduled appointments."
            )}
            body={t(
              "home.doctor.appointments.emptyBody",
              "Approved appointments will appear here once patients confirm them."
            )}
          />
        )}
      </section>

      {/* Elevate */}
      <div className="text-title-medium text-on-surface-var mb-3">
        {t("home.doctor.elevate.title", "Elevate your Consultancy")}
      </div>

      <section className="mt-2 mb-10">
        <Card
          as="button"
          type="button"
          onClick={() => nav("/settings/doctor/p/gallery")}
          className="
            w-full text-left
            px-4
            py-5
            flex
            items-center
            justify-between
            gap-3
          "
        >
          <div className="flex-1 min-w-0">
            <div className="text-title-medium text-on-surface mb-1">
              {t("home.doctor.gallery.title", "Make your profile standout")}
            </div>
            <div className="text-body-medium text-on-surface-var">
              {doctorGalleryIsEmpty
                ? t("home.doctor.gallery.body", "Add images about your consultancy")
                : t(
                    "home.doctor.gallery.manageBody",
                    "Keep your consultancy photos current and visible"
                  )}
            </div>

            {showDoctorGalleryNudge && (
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  snoozeDoctorGalleryNudge(14);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    snoozeDoctorGalleryNudge(14);
                  }
                }}
                className="mt-2 inline-block text-label-large text-on-surface-var hover:text-on-surface"
              >
                {t("home.doctor.gallery.notNow", "Not now")}
              </span>
            )}
          </div>

          <div className="shrink-0 flex items-center justify-center">
            <img
              src={ICON_PROFILE_ADD_PHOTO}
              alt=""
              className="h-16 w-16"
              draggable="false"
            />
          </div>
        </Card>
      </section>

      {/* Regional health */}
      <div className="text-title-medium text-on-surface-var mb-3">
        {t("home.doctor.regional.title", "Regional health")}
      </div>

      <section className="mt-2 mb-10">
        <Card
          as="button"
          type="button"
          onClick={() => nav("/regional-health")}
            className="
              w-full text-left
              px-4
              py-5
            flex
            items-center
            justify-between
            gap-3
          "
        >
          <div className="w-full flex flex-col items-center text-center gap-2">
            <img
              src={ICON_CONDITIONS}
              alt=""
              className="h-16 w-16"
              draggable="false"
            />

            <div className="text-title-medium text-on-surface">
              {t("home.doctor.regional.headline", "Stay informed of the region's")}
            </div>

            <div className="text-body-medium text-on-surface-var">
              {regionalHealthSummary}
            </div>
          </div>

          <span className="w-6 h-6 shrink-0" aria-hidden="true" />
        </Card>
      </section>
    </main>
  </div>
);
}
